import React, { lazy } from 'react';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { GoogleReviews } from '../components/GoogleReviews';
import { CallbackForm } from '../components/CallbackForm';
import { MapPin, MessageCircle, Mail, Calendar, Instagram, Phone } from 'lucide-react';
import { useTranslation } from '../i18n';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
const INSTAGRAM_URL = 'https://instagram.com/dentico_clinic';
const MAPS_EMBED_URL = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1241.0181846982155!2d-0.104187760804084!3d51.53089276153758!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48760f7c6534e077%3A0xa54a590bd3b2a149!2sDentico%20Clinic%20-%20Private%20Dentist%20in%20Angel%2C%20Islington!5e0!3m2!1suk!2suk!4v1769722166469!5m2!1suk!2suk';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-1249">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-1250" />
    </svg>;
}
export function ContactPage() {
  const {
    t,
    lang
  } = useTranslation();
  return <Layout data-id="element-1251">
      <div className="bg-teal-900 py-20 text-white" data-id="element-1252">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1253">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1254">
            {t('contact.title')}
          </h1>
          <p className="text-xl text-teal-100 max-w-2xl mx-auto" data-id="element-1255">
            {t('contact.subtitle')}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-1256">
        <div className="grid lg:grid-cols-3 gap-12" data-id="element-1257">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-8" data-id="element-1258">
            <Card className="p-8" data-id="element-1259">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1260">
                {t('contact.getInTouch')}
              </h2>

              <div className="space-y-6" data-id="element-1261">
                {/* Address */}
                <div className="flex items-start gap-4" data-id="element-1262">
                  <div className="p-2 bg-teal-50 rounded-lg text-teal-600" data-id="element-1263">
                    <MapPin className="w-6 h-6" data-id="element-1264" />
                  </div>
                  <div data-id="element-1265">
                    <h3 className="font-semibold text-gray-900 mb-1" data-id="element-1266">
                      {t('contact.address')}
                    </h3>
                    <p className="text-gray-600" data-id="element-1267">
                      360–364 City Rd
                      <br data-id="element-1268" />
                      London EC1V 2PY
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4" data-id="element-1269">
                  <div className="p-2 bg-teal-50 rounded-lg text-teal-600" data-id="element-1270">
                    <Phone className="w-6 h-6" data-id="element-1271" />
                  </div>
                  <div data-id="element-1272">
                    <h3 className="font-semibold text-gray-900 mb-1" data-id="element-1273">
                      {lang === 'en' ? 'Phone' : lang === 'ua' ? 'Телефон' : lang === 'ro' ? 'Telefon' : 'Телефон'}
                    </h3>
                    <a href="tel:+447345197820" className="text-teal-600 hover:underline font-medium" data-id="element-1274">
                      07345 197820
                    </a>
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="flex items-start gap-4" data-id="element-1275">
                  <div className="p-2 bg-teal-50 rounded-lg text-teal-600" data-id="element-1276">
                    <MessageCircle className="w-6 h-6" data-id="element-1277" />
                  </div>
                  <div data-id="element-1278">
                    <h3 className="font-semibold text-gray-900 mb-1" data-id="element-1279">
                      {t('contact.whatsapp')}
                    </h3>
                    <p className="text-gray-600 mb-2" data-id="element-1280">
                      {t('contact.whatsappDesc')}
                    </p>
                    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-1281">
                      <Button variant="outline" size="sm" className="gap-2" data-id="element-1282">
                        <MessageCircle className="w-4 h-4" data-id="element-1283" />
                        {t('contact.chatOnWhatsapp')}
                      </Button>
                    </a>
                  </div>
                </div>

                {/* Telegram */}
                <div className="flex items-start gap-4" data-id="element-1284">
                  <div className="p-2 bg-teal-50 rounded-lg text-teal-600" data-id="element-1285">
                    <TelegramIcon className="w-6 h-6" data-id="element-1286" />
                  </div>
                  <div data-id="element-1287">
                    <h3 className="font-semibold text-gray-900 mb-1" data-id="element-1288">
                      Telegram
                    </h3>
                    <p className="text-gray-600 mb-2" data-id="element-1289">
                      {lang === 'en' ? 'Message us on Telegram' : lang === 'ua' ? 'Напишіть нам у Telegram' : lang === 'ro' ? 'Scrieți-ne pe Telegram' : 'Напишите нам в Telegram'}
                    </p>
                    <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" data-id="element-1290">
                      <Button variant="outline" size="sm" className="gap-2" data-id="element-1291">
                        <TelegramIcon className="w-4 h-4" data-id="element-1292" />
                        Telegram
                      </Button>
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4" data-id="element-1293">
                  <div className="p-2 bg-teal-50 rounded-lg text-teal-600" data-id="element-1294">
                    <Mail className="w-6 h-6" data-id="element-1295" />
                  </div>
                  <div data-id="element-1296">
                    <h3 className="font-semibold text-gray-900 mb-1" data-id="element-1297">
                      {t('contact.email')}
                    </h3>
                    <a href="mailto:info@denticoclinic.co.uk" className="text-teal-600 hover:underline break-all" data-id="element-1298">
                      info@denticoclinic.co.uk
                    </a>
                  </div>
                </div>

                {/* Instagram */}
                <div className="flex items-start gap-4" data-id="element-1299">
                  <div className="p-2 bg-teal-50 rounded-lg text-teal-600" data-id="element-1300">
                    <Instagram className="w-6 h-6" data-id="element-1301" />
                  </div>
                  <div data-id="element-1302">
                    <h3 className="font-semibold text-gray-900 mb-1" data-id="element-1303">
                      {t('contact.instagram')}
                    </h3>
                    <p className="text-gray-600 mb-2" data-id="element-1304">
                      {t('contact.instagramDesc')}
                    </p>
                    <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="text-teal-600 font-medium hover:underline" data-id="element-1305">
                      @dentico_clinic
                    </a>
                  </div>
                </div>
              </div>

              {/* Book Online Button */}
              <div className="mt-8 pt-6 border-t border-gray-100" data-id="element-1306">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-1307">
                  <Button fullWidth size="lg" className="gap-2" data-id="element-1308">
                    <Calendar className="w-5 h-5" data-id="element-1309" />
                    {t('nav.bookOnline')}
                  </Button>
                </a>
              </div>
            </Card>

            <Card className="p-8" data-id="element-1310">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1311">
                {t('contact.openingHours')}
              </h2>
              <div className="space-y-3" data-id="element-1312">
                <div className="flex justify-between items-center pb-3 border-b border-gray-100" data-id="element-1313">
                  <span className="text-gray-600" data-id="element-1314">{t('footer.monFri')}</span>
                  <span className="font-medium text-gray-900" data-id="element-1315">
                    10:00 - 18:00
                  </span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-100" data-id="element-1316">
                  <span className="text-gray-600" data-id="element-1317">{t('footer.saturday')}</span>
                  <span className="font-medium text-gray-900" data-id="element-1318">
                    10:00 - 18:00
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-1319">
                  <span className="text-gray-600" data-id="element-1320">{t('footer.sunday')}</span>
                  <span className="font-medium text-red-500" data-id="element-1321">
                    {t('footer.closed')}
                  </span>
                </div>
              </div>
            </Card>
          </div>

          {/* Map */}
          <div className="lg:col-span-2" data-id="element-1322">
            <div className="bg-gray-100 rounded-2xl overflow-hidden h-full min-h-[500px] shadow-lg" data-id="element-1323">
              <iframe src={MAPS_EMBED_URL} width="100%" height="100%" style={{
              border: 0,
              minHeight: '500px'
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Dentico Clinic Location" data-id="element-1324"></iframe>
            </div>
          </div>
        </div>
      </div>

      {/* Callback Form Section */}
      <section className="py-16 bg-gray-50" data-id="element-1325">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1326">
          <CallbackForm data-id="element-1327" />
        </div>
      </section>

      <GoogleReviews data-id="element-1328" />
    </Layout>;
}